import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';

import './tutorial.scss';

/**
 * The "Learn Medsender" checklist.
 *
 * Presentational on purpose: it takes resolved tutorials (each already carrying
 * `completed` / `available`) and reports intent upward, which keeps the panel
 * trivially testable and leaves persistence to LearnMedsender.
 */
const TutorialMenu = ({
  tutorials,
  onStart,
  onClose,
  onDismiss,
  completedCount,
}) => {
  const total = tutorials.length;

  const renderTutorial = (tutorial) => {
    const { completed, available } = tutorial;
    const itemClass = [
      'tutorial-menu__item',
      completed ? 'is-complete' : '',
      available ? '' : 'is-unavailable',
    ].filter(Boolean).join(' ');

    const item = (
      <button
        type="button"
        className={itemClass}
        disabled={!available}
        onClick={() => onStart(tutorial.id)}
      >
        <span
          className="tutorial-menu__check"
          aria-hidden="true"
        >
          {completed ? '✓' : ''}
        </span>
        <span className="tutorial-menu__item-text">
          <span className="tutorial-menu__item-title">{tutorial.title}</span>
          <span className="tutorial-menu__item-description">{tutorial.description}</span>
        </span>
        <span className="tutorial-menu__item-action">
          {completed ? 'Replay' : 'Start'}
        </span>
      </button>
    );

    return (
      <li key={tutorial.id}>
        {available
          ? item
          : (
            // Disabled buttons swallow pointer events, so the explanation is
            // wrapped rather than attached to the button itself.
            //
            // The wrapper span is OURS on purpose. Handing antd's Tooltip a
            // disabled <button> directly triggers its
            // `getDisabledCompatibleChildren` path (antd 4.17.4
            // tooltip/index.js), which wraps the button in a span, MOVES the
            // button's className onto that span and sets `className: null` on
            // the button. The row then loses `tutorial-menu__item` completely
            // and renders with UA button styling — a grey, centre-aligned,
            // shrink-to-fit box — while the span's inline `display:inline-block`
            // overrides the stylesheet's `display:flex`. Wrapping in a span
            // ourselves keeps the Tooltip's child a non-button, so antd leaves
            // our markup alone. `pointer-events: none` on the button (see
            // tutorial.scss) is what antd's wrapper was providing: it lets the
            // hover reach this span so the tooltip still opens.
            <Tooltip title={tutorial.unavailableReason}>
              <span className="tutorial-menu__item-wrap">{item}</span>
            </Tooltip>
          )}
      </li>
    );
  };

  return (
    <dialog open className="tutorial-menu" aria-label="Learn Medsender" data-tutorial-menu>
      <div className="tutorial-menu__header">
        <div>
          <h2 className="tutorial-menu__title">Learn Medsender</h2>
          <p className="tutorial-menu__subtitle">
            {`${completedCount} of ${total} complete — short, hands-on walkthroughs you click through yourself.`}
          </p>
        </div>
        <button
          type="button"
          className="tutorial-menu__close"
          aria-label="Close Learn Medsender"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <ul className="tutorial-menu__list">
        {/*
          One flat list, in definition order. The core/extra split still drives
          `has_completed_tutorial` (see LearnMedsender) but is deliberately not
          surfaced here — the divider only added a distinction the reader does
          not need to act on.
        */}
        {tutorials.map(renderTutorial)}
      </ul>

      <div className="tutorial-menu__footer">
        <button
          type="button"
          className="tutorial-menu__dismiss"
          onClick={onDismiss}
        >
          Don&apos;t show this again
        </button>
        <span className="tutorial-menu__footer-note">
          You can reopen this anytime from your profile or mobile menu by selecting Learn Medsender.
        </span>
      </div>
    </dialog>
  );
};

TutorialMenu.defaultProps = {
  completedCount: 0,
};

TutorialMenu.propTypes = {
  tutorials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    completed: PropTypes.bool,
    available: PropTypes.bool,
    unavailableReason: PropTypes.string,
    core: PropTypes.bool,
  })).isRequired,
  onStart: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  completedCount: PropTypes.number,
};

export default TutorialMenu;
